/**
 * Wholesale PDF Generator
 * Handles PDF generation specifically for Wholesale strategy
 */

import { handleImageUrl } from '../utils/pdfUtils'
import { currencyFormat } from '@/utils/number'
import {
  sum,
} from '@/utils/lodash'
import { getStrategyName } from '@/utils/stratagies'
import { BasePDFGenerator } from './BasePDFGenerator'
import { buildProformaDescriptionPdfNode } from '@/utils/propertyDescription'

export class WholesalePDFGenerator extends BasePDFGenerator {
  async generatePDF() {
    return this.generateSinglePageDocDefinition()
  }

  /**
   * Generate page 1 lower right section
   */
  async generatePage1LowerRight() {
    const { property } = this
    const {
      beds = [0],
      baths = [0],
      sqft = [0],
      acquisition = {},
      assumptions = {},
      portfolio = {},
    } = property
    const { organization = {} } = portfolio

    const whiteImage = await handleImageUrl('/images/white.jpg')

    let orgLogo = property.proformaLogo
      ? await handleImageUrl(property.proformaLogo)
      : organization.logo
        ? organization.logo.url
          ? await handleImageUrl(organization.logo.url)
          : whiteImage
        : whiteImage

    if (!orgLogo) orgLogo = whiteImage

    const pepMaxOfferPrice =
      (acquisition.analytics.wholesale.arvTotalWholesale *
        assumptions.percentageOfARVWholesale) /
        100 -
      acquisition.analytics.wholesale.rehabDollar
    const buyerProfit = acquisition.analytics.wholesale
      .arvTotalWholesale
      ? acquisition.analytics.wholesale.arvTotalWholesale -
        (pepMaxOfferPrice +
          acquisition.analytics.wholesale.assignmentFeeDollar +
          acquisition.analytics.wholesale.rehabDollar +
          acquisition.analytics.wholesale.arvTotalWholesale * 0.16)
      : 0

    const saleProceeds = acquisition.analytics.wholesale
      .assignmentFeeDollar
      ? acquisition.analytics.wholesale.assignmentFeeDollar
      : 0

    const afterRepairValue =
      acquisition.analytics.wholesale.arvTotalWholesale || 0
    const rehab = acquisition.analytics.wholesale.rehabDollar || 0
    const closingCost =
      acquisition.analytics.wholesale.closingCostWholesaleDollar || 0

    const sqftString = sum(sqft)
      ? sum(sqft).toLocaleString('en')
      : '0'

    return [
      {
        text: ['Financial ', { text: 'Returns', color: '#3399ff' }],
        fontSize: 18,
        margin: [0, 5, 0, 0],
      },
      {
        canvas: [
          {
            type: 'line',
            x1: 0,
            y1: 5,
            x2: 150,
            y2: 5,
            lineWidth: 2,
          },
          {
            type: 'rect',
            x: 2,
            y: 27,
            w: 175,
            h: 55,
            r: 2,
            color: 'black',
            fillOpacity: 0.5,
          },
          {
            type: 'rect',
            x: 0,
            y: 25,
            w: 175,
            h: 55,
            r: 2,
            color: '#3399ff',
            fillOpacity: 1,
          },
          {
            type: 'rect',
            x: 192,
            y: 27,
            w: 175,
            h: 55,
            r: 2,
            color: 'black',
            fillOpacity: 0.5,
          },
          {
            type: 'rect',
            x: 190,
            y: 25,
            w: 175,
            h: 55,
            r: 2,
            color: '#344265',
            fillOpacity: 1,
          },
          {
            type: 'rect',
            x: 2,
            y: 92,
            w: 365,
            h: 92,
            r: 2,
            color: 'black',
            fillOpacity: 0.5,
          },
          {
            type: 'rect',
            x: 0,
            y: 90,
            w: 365,
            h: 92,
            r: 2,
          },
          {
            type: 'rect',
            x: 0,
            y: 90,
            w: 365,
            h: 92,
            r: 2,
            border: [true, true, true, true],
            color: 'white',
            fillOpacity: 1,
          },
        ],
      },
      {
        absolutePosition: { x: 220, y: 360 },
        stack: [
          { text: 'Net Profit', fontSize: 15, color: 'white' },
          {
            canvas: [
              {
                type: 'line',
                x1: 0,
                y1: 0,
                x2: 100,
                y2: 0,
                lineWidth: 1.5,
                color: 'white',
              },
            ],
          },
          {
            text: [
              {
                text: currencyFormat.format(saleProceeds),
                fontSize: 14,
                bold: true,
              },
            ],
            color: 'white',
            margin: [0, 7, 0, 0],
          },
        ],
      },
      {
        absolutePosition: { x: 410, y: 360 },
        stack: [
          { text: 'Buyer Profit', fontSize: 15, color: 'white' },
          {
            canvas: [
              {
                type: 'line',
                x1: 0,
                y1: 0,
                x2: 100,
                y2: 0,
                lineWidth: 1.5,
                color: 'white',
              },
            ],
          },
          {
            text: [
              {
                text: currencyFormat.format(buyerProfit),
                fontSize: 14,
                bold: true,
              },
            ],
            color: 'white',
            margin: [0, 7, 0, 0],
          },
        ],
      },
      {
        absolutePosition: { x: 220, y: 425 },
        stack: [
          {
            text: [
              getStrategyName('Wholesale', organization?.member),
            ],
            fontSize: 16,
            alignment: 'left',
          },
        ],
      },
      {
        absolutePosition: { x: 220, y: 446 },
        stack: [
          {
            text: [
              'ARV:   ',
              {
                text: currencyFormat.format(afterRepairValue),
                fontSize: 14,
                bold: true,
              },
            ],
            fontSize: 13,
            alignment: 'left',
          },
        ],
      },
      {
        absolutePosition: { x: 220, y: 462 },
        stack: [
          {
            text: [
              'Rehab:   ',
              {
                text: currencyFormat.format(rehab),
                fontSize: 14,
                bold: true,
              },
            ],
            fontSize: 13,
            alignment: 'left',
          },
        ],
      },
      {
        absolutePosition: { x: 220, y: 478 },
        stack: [
          {
            text: [
              'Closing Cost:  ',
              {
                text: currencyFormat.format(closingCost),
                fontSize: 14,
                bold: true,
              },
            ],
            fontSize: 13,
            alignment: 'left',
          },
        ],
      },
      {
        absolutePosition: { x: 220, y: 494 },
        stack: [
          {
            text: [
              'Resale Price:   ',
              {
                text: currencyFormat.format(pepMaxOfferPrice),
                fontSize: 14,
                bold: true,
              },
            ],
            fontSize: 13,
            alignment: 'left',
          },
        ],
      },
      {
        text: [
          'Property ',
          { text: 'Information', color: '#3399ff' },
        ],
        fontSize: 18,
        margin: [0, 20, 0, 0],
      },
      {
        canvas: [
          {
            type: 'line',
            x1: 0,
            y1: 5,
            x2: 150,
            y2: 5,
            lineWidth: 2,
          },
        ],
      },
      {
        columns: [
          {
            type: 'square',
            color: '#3399ff',
            ul: [
              {
                text: [
                  'Beds:   ',
                  { text: sum(beds), fontSize: 14, bold: true },
                ],
                color: 'black',
                fontSize: 14,
                margin: 5,
              },
              {
                text: [
                  'Baths:   ',
                  { text: sum(baths), fontSize: 14, bold: true },
                ],
                color: 'black',
                fontSize: 14,
                margin: 5,
              },
              // {
              //   text: [
              //     'Sq Feet:   ',
              //     {
              //       text: sqftString,
              //       fontSize: 14,
              //       bold: true,
              //     },
              //   ],
              //   color: 'black',
              //   fontSize: 14,
              //   margin: 5,
              // },
            ],
            margin: [0, 10, 0, 0],
            width: 140,
          },
          {
            type: 'square',
            color: '#3399ff',
            ul: [
              {
                text: [
                  'Sq Feet:   ',
                  {
                    text: sqftString,
                    fontSize: 14,
                    bold: true,
                  },
                ],
                color: 'black',
                fontSize: 14,
                margin: 5,
              },
              {
                text: [
                  'Year:   ',
                  {
                    text: property.year,
                    fontSize: 14,
                    bold: true,
                  },
                ],
                color: 'black',
                fontSize: 14,
                margin: 5,
              },
            ],
            margin: [0, 10, 0, 0],
            width: 'auto',
          },
        ],
      },
      buildProformaDescriptionPdfNode(property),
    ]
  }
}
